import androidx.compose.runtime.*
import net.sergeych.ugdx.shared.ApiException
import net.sergeych.ugdx.shared.Currency
import net.sergeych.ugdx.shared.api.ApiAccount
import components.TButton
import components.TForm
import components.TLink
import components.TTextInput
import frontend.App
import frontend.components.Modal
import frontend.lib.classList
import frontend.lib.displayCurrencyAliases
import frontend.lib.displayCurrencyCode
import frontend.models.User
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.*

class AccountSelectCAttrsBuilder {
    var onChange: (value: ApiAccount?) -> Unit = {}
    var label: @Composable() () -> Unit = {}
    var options: List<ApiAccount> = emptyList()
    var isDisabled = false
    var isCondensed = false

    fun onChange(handler: (value: ApiAccount?) -> Unit) { onChange = handler }
    fun label(l: @Composable() () -> Unit) { label = l }
    fun disabled() { isDisabled = true }
    fun options(list: List<ApiAccount>) { options = list }
    fun condensed() { isCondensed = true }
}

@Composable
fun AccountSelectByCurrency(
    id: String,
    user: User,
    currency: Currency,
    selected: ApiAccount?,
    attrs: AccountSelectCAttrsBuilder.() -> Unit = {}
) {
    var isModalOpened by remember { mutableStateOf(false) }
    val builder = AccountSelectCAttrsBuilder()
    attrs.invoke(builder)

    var available = user.accounts.filter { it.currencyCode == currency.code }

    Div() {
        if (!builder.isCondensed) Label(forId = id, attrs = {
            classes("block", "text-sm", "font-medium", "text-gray-700")
        }) {
            if (available.size == 0) {
                Text("You have no ${displayCurrencyCode(currency)} accounts. ")
                TLink("Create one") { isModalOpened = true }
            } else {
                Text("Select ${displayCurrencyCode(currency)} account")
//                TLink("create one") { isModalOpened = true }
            }
        }

        Select(attrs = {
            id(id)
            name("select")
            classes("mt-1", "block", "w-full", "pl-3", "pr-10",
                if (builder.isCondensed) "py-1" else "py-2",
                "text-base", "border-gray-300", "focus:outline-none", "focus:ring-indigo-500", "focus:border-indigo-500", "sm:text-sm", "rounded-md")
            onChange {
                val code = it.value
                val acc = available.find { it.id.toString() == code }
                builder.onChange(acc)
            }
        }) {
            available.forEach {
                val option = it

                Option(option.id.toString(), attrs = {
                    if (selected?.id == option.id) selected()
                }) { Text(displayCurrencyAliases(option.toString())) }
            }
        }
    }

    if (isModalOpened) CreateAccountModal(user, currency) {
        it?.let { builder.onChange(it) }
        isModalOpened = false
    }
}

@Composable
fun CreateAccountModal(
    user: User,
    currency: Currency,
    onClose: (account: ApiAccount?) -> Unit
) {
    val scope = rememberCoroutineScope()
    var name: String? by mutableStateOf(null)
    var error: String? by mutableStateOf(null)

    fun createAccount(name: String?) {
        error = when {
            name == null -> "Name cannot be empty"
            else -> {
                val existing = user.accounts.find {
                    it.currencyCode == currency.code && it.name == name
                }
                if (existing != null) "Account with this name and currency already exists"
                else {
                    scope.launch {
                        try {
                            val account = user.createAccount(currency, name)
                            onClose(account)
                        } catch(err: ApiException) {
                            error = err.message
                        }
                    }
                    null
                }
            }
        }
    }

    Modal(onClose = { onClose(null) }) {
        TForm() {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text("Create account")
            }

            TTextInput("name", name ?: "") {
                label { Text("Account name") }
                onInput {
                    if (it == "") name = null
                    else name = it
                }
            }

            if (error !== null) P(attrs = { classes("text-red-600") }) {
                Text(error ?: "")
            }

            TButton(attrs = { onClick { createAccount(name) } }) {
                Text("Create account")
            }
        }
    }
}
